import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useQuery, NetworkStatus } from '@apollo/client';
import Container from '@/components/Container';
import Link from '@/components/Link';
import LoadingIndicator from '@/components/LoadingIndicator';
import { QuantitySelector, useCart } from '@/components/ShoppingCart';
import { notifySuccess } from '@/components/Toaster';
import { createApolloClient } from '@/apollo/client';
import { withAuthenticatedUser } from '@/components/ProtectedPage';
import GET_CERTIFICATIONS_QUERY from '@/graphql/queries/certificationProductsListing.graphql';
import clsx from 'clsx';

function CertificationsPageSkeletonItem() {
  return (
    <div className="shadow rounded-lg lg:rounded-xl overflow-hidden bg-white md:col-span-6 flex ring-1 ring-black ring-opacity-10">
      <div className="flex items-stretch w-full">
        <div className="w-40 bg-gray-100 animate-pulse" />
        <div className="flex-1 flex flex-col p-4 lg:p-6">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-2">
              <div className="h-6 w-11/12 bg-gray-100 animate-pulse" />
              <div className="h-6 w-9/12 bg-gray-100 animate-pulse" />
            </div>
            <div className="h-6 bg-gray-100 animate-pulse w-32" />
          </div>
          <div className="space-y-4 py-4">
            <div className="flex flex-col space-y-2 flex-1">
              <div className="h-2.5 bg-gray-100 animate-pulse" />
              <div className="h-2.5 bg-gray-100 animate-pulse w-11/12" />
              <div className="h-2.5 bg-gray-100 animate-pulse w-10/12" />
              <div className="h-2.5 bg-gray-100 animate-pulse w-9/12" />
              <div className="h-2.5 bg-gray-100 animate-pulse w-8/12" />
            </div>
            <div className="h-4 bg-gray-100 animate-pulse w-20" />
          </div>
          <div className="h-px bg-gray-100 animate-pulse w-full" />
          <div className="flex items-center pt-4">
            <div className="flex-1 flex items-center justify-end space-x-3">
              <div className="h-8 w-28 bg-gray-100 animate-pulse rounded-full" />
              <div className="h-8 w-28 bg-gray-100 animate-pulse rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CertificationsPageSkeleton() {
  return (
    <div className="flex w-full py-10  lg:pb-20 bg-gray-100 from-gray-100 to-white relative">
      <Container className="space-y-10">
        <div>
          <h1 className="text-3xl lg:text-5xl font-display font-extrabold tracking-tight">
            Certificaciones
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <CertificationsPageSkeletonItem />
          <CertificationsPageSkeletonItem />
          <CertificationsPageSkeletonItem />
          <CertificationsPageSkeletonItem />
        </div>
      </Container>
    </div>
  );
}

const isDevelopment = process.env.NODE_ENV !== 'production';

function CertificationsPageError({ error }) {
  return (
    <div className="flex w-full py-10 lg:pb-20 relative bg-gray-100 from-gray-100 to-white">
      <Container className="relative z-20 space-y-10">
        <div>
          <h1 className="text-3xl lg:text-5xl font-display font-extrabold tracking-tight">
            Certificaciones
          </h1>
        </div>
        <div className="flex flex-col space-y-4">
          {error.graphQLErrors.map((err, idx) => (
            <div
              key={`graphql-error-${idx}`}
              className="bg-red-50 rounded ring-1 ring-red-400 shadow flex flex-col overflow-hidden"
            >
              <div className="px-4 py-2 flex items-start space-x-1.5 rounded">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-red-500 mt-px"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="font-medium flex-1">{err.message}</div>
              </div>
              {isDevelopment && (
                <div className="border-t border-red-300 bg-white px-4 py-2 flex flex-col">
                  {err.debugMessage && (
                    <div className="flex items-center">
                      <div className="font-semibold leading-4 text-sm">
                        Mensaje de depuración:
                      </div>
                      &nbsp;&nbsp;
                      <div className="font-mono text-sm pt-0.5">
                        {err.debugMessage}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
}

CertificationsPageError.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
  }).isRequired,
};

function CertificationsPageItem({
  certification,
  product,
  isAuthenticated,
  apolloClient,
}) {
  const [qty, setItemQuantity] = useState(1);
  const [initialQuantity, setInitialQuantity] = useState(1);

  const cart = useCart();

  const onAddItemToCartClick = useCallback(
    (quantity = 1) => {
      if (!isAuthenticated) {
        // Add to cart is only allowed for authenticated users
        return;
      }

      if (quantity < 1) {
        notifyError(
          `Debe indicar la cantidad de unidades ${product.name} que desea añadir al carrito.`
        );
        return;
      }

      setInitialQuantity(quantity);

      if (cart.isEmpty || !cart.inCart(product.productId)) {
        cart.addItem(
          {
            ...product,
            id: product.productId,
          },
          quantity,
          apolloClient,
          () => {
            notifySuccess(
              quantity === 1
                ? `Se ha añadido al carrito una unidad de ${product.name}.`
                : `Se han añadido al carrio ${quantity} unidades de ${product.name}.`
            );
            setInitialQuantity(1);
          }
        );
      } else {
        const itm = cart.getItem(product.productId);
        if (itm) {
          const newQuantity = itm.quantity + quantity;
          cart.updateItemQuantity(itm.id, newQuantity, apolloClient, () => {
            notifySuccess(
              `Se ha actualizado el carrito con ${quantity} ${
                quantity === 1 ? 'unidad' : 'unidades'
              } más de ${product.name}, para un total de ${newQuantity} ${
                newQuantity === 1 ? 'unidad' : 'unidades'
              }.`
            );
            setInitialQuantity(1);
          });
        }
      }
    },
    [product, cart, apolloClient, isAuthenticated]
  );

  useEffect(() => {
    if (cart.isItemUpdating(product.productId)) {
      setTimeout(() => cart.finishUpdatingItem(product.productId), 2000);
    }
  }, [product, cart]);

  const isUpdatingCart = cart.isItemUpdating(product.productId);

  const certificationDetailsLink = (
    <Link
      href={certification.uri}
      className="inline-flex items-center space-x-px text-green-500 hover:text-white cursor-pointer focus:ring-4 rounded-full focus:ring-green-500 focus:ring-opacity-10 focus:bg-green-600 focus:text-white pl-6 pr-4 py-1 hover:bg-green-500"
    >
      <div className="font-medium lg:text-lg lg:font-semibold">
        Más información
      </div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 text-current"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
    </Link>
  );

  return (
    <div className="shadow rounded-lg lg:rounded-xl xl:rounded-2xl overflow-hidden bg-white md:col-span-6 flex ring-1 ring-black ring-opacity-10">
      <div className="flex items-stretch lg:flex-col w-full">
        <div className="w-40 lg:w-auto bg-gray-50 bg-gradient-to-t from-grady-50 to-white relative py-6 lg:p-8 flex items-start justify-center lg:items-center lg:justify-start lg:px-6 lg:space-x-6 lg:bg-gradient-to-t lg:from-gray-100 lg:to-gray-50">
          <Link href={certification.uri} title="Más información">
            <Image
              alt={
                certification.featuredImage.node.alt ||
                certification.featuredImage.node.title
              }
              src={certification.featuredImage.node.sourceUrl}
              className="h-auto rounded-full relative z-20"
              width={160}
              height={160}
            />
          </Link>
          <div className="hidden lg:flex flex-col space-y-1 lg:space-y-4 flex-1 pr-5">
            <Link className="focus:outline-none" href={certification.uri}>
              <div className="text-xl lg:text-2xl xl:text-3xl">
                <h3 className="lg:tracking-tight font-semibold lg:font-bold xl:font-medium leading-snug">
                  {certification.title}
                </h3>
              </div>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="text-lg lg:text-xl xl:text-2xl font-normal text-green-500">
                {product.price}
              </div>
            </div>
          </div>
        </div>
        <div className="h-px w-full hidden lg:block bg-gray-300" />
        <div className="flex-1 p-4 md:p-6 lg:p-0 flex flex-col divide-y lg:divide-none space-y-4 lg:space-y-0">
          <div className="flex flex-col space-y-1 lg:space-y-4 lg:p-6 lg:hidden">
            <Link className="focus:outline-none" href={certification.uri}>
              <div className="text-xl lg:text-2xl xl:text-3xl">
                <h3 className="lg:tracking-tight font-semibold lg:font-bold xl:font-medium leading-snug">
                  {certification.title}
                </h3>
              </div>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="text-lg lg:text-xl xl:text-2xl font-normal text-green-500">
                {product.price}
              </div>
            </div>
          </div>
          <div className="flex-1 flex flex-col pt-4 space-y-4 lg:py-6 lg:px-10">
            <div
              className="text-gray-600 text-sm flex-1 leading-relaxed lg:text-lg"
              dangerouslySetInnerHTML={{
                __html: certification.excerpt,
              }}
            />
            {isAuthenticated && <div>{certificationDetailsLink}</div>}
          </div>
          <div className="h-px w-full hidden lg:block bg-gray-200" />
          <div className="flex items-center pt-4 space-x-4 lg:p-6">
            <div className="flex-1 flex items-center justify-end">
              {isAuthenticated ? (
                <div className="flex items-center space-x-2 lg:space-x-6">
                  <QuantitySelector
                    disable={isUpdatingCart}
                    initialQuantity={initialQuantity}
                    onQuantityUpdated={(qty) => setItemQuantity(qty)}
                  />
                  <button
                    className={clsx(
                      'rounded-full bg-green-500 px-5 lg:px-8 py-1 inline-flex relative items-center font-medium text-white leading-6 hover:bg-green-600 focus:ring-4 focus:ring-green-200 focus:bg-green-600 lg:py-2 lg:text-lg lg:leading-7 lg:font-semibold',
                      isUpdatingCart && 'pointer-events-none opacity-50'
                    )}
                    onClick={() => onAddItemToCartClick(qty)}
                    type="button"
                  >
                    {isUpdatingCart && (
                      <div className="absolute inset-0 justify-center items-center flex text-white">
                        <div>
                          <LoadingIndicator color="text-white" />
                        </div>
                      </div>
                    )}
                    <div className={clsx(isUpdatingCart && 'invisible')}>
                      Añadir al carrito
                    </div>
                  </button>
                </div>
              ) : (
                certificationDetailsLink
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CertificationsPageFinal({ isAuthenticated, apolloClient }) {
  const { loading, error, data, networkStatus } = useQuery(
    GET_CERTIFICATIONS_QUERY,
    {
      client: apolloClient,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
    }
  );

  const isRefetching = networkStatus === NetworkStatus.refetch;

  if (loading && !isRefetching) {
    return <CertificationsPageSkeleton />;
  }

  if (error) {
    return <CertificationsPageError error={error} />;
  }

  const {
    certifications: { nodes: certifications },
  } = data;

  return (
    <div className="flex w-full py-10 lg:pb-20 relative bg-gray-100 from-gray-100 to-white">
      <Container className="relative z-20 space-y-10">
        <div>
          <h1 className="text-3xl lg:text-5xl font-display font-extrabold tracking-tight">
            Certificaciones
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {certifications.map((certification) => {
            const product = certification.info.product;
            if (product === null) {
              return null;
            }

            return (
              <CertificationsPageItem
                key={certification.id}
                certification={certification}
                product={product}
                isAuthenticated={isAuthenticated}
                apolloClient={apolloClient}
              />
            );
          })}
        </div>
      </Container>
    </div>
  );
}

function CertificationsPage({ loadingCurrentUser, user, ...rest }) {
  if (loadingCurrentUser) {
    return <CertificationsPageSkeleton />;
  }

  return (
    <CertificationsPageFinal
      apolloClient={createApolloClient(null, user?.accessToken || null)}
      {...rest}
    />
  );
}

export default withAuthenticatedUser(CertificationsPage, {
  doNotRedirectToLogin: true,
});
